import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const CHILD_BG =
  "https://customer-assets.emergentagent.com/job_4e9fd76d-07f5-44bf-820b-850786358f58/artifacts/cd6xeng3_leo-rivas-wtxcaDIdOCM-unsplash.jpg";

const services = [
  {
    icon: "🧒",
    title: "Child Behavior Assessment",
    desc: "Comprehensive evaluation of behavioral patterns and emotional responses in children.",
  },
  {
    icon: "🎲",
    title: "Play Therapy",
    desc: "Therapeutic healing through structured, guided play sessions tailored to each child.",
  },
  {
    icon: "📚",
    title: "Learning Disabilities Support",
    desc: "Specialized help for children with dyslexia, ADHD, and other learning challenges.",
  },
  {
    icon: "🤝",
    title: "Parent-Child Relationship Counseling",
    desc: "Strengthening the emotional bond and communication between parents and children.",
  },
  {
    icon: "💖",
    title: "Emotional Development",
    desc: "Building emotional intelligence, resilience, and coping skills in growing minds.",
  },
  {
    icon: "🌱",
    title: "Child Anxiety & Depression Therapy",
    desc: "Gentle, evidence-based therapy for young minds facing emotional struggles.",
  },
];

export default function ChildPsychology() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const visible = rect.top < window.innerHeight && rect.bottom > 0;
      if (visible) {
        // Parallax: image moves at 0.5x scroll speed relative to section start
        const sectionTop = rect.top;
        setOffset(-sectionTop * 0.3);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="child-psychology"
      data-testid="child-psychology-section"
      ref={ref}
      className="relative py-28 md:py-36 px-6 overflow-hidden"
    >
      {/* Parallax background */}
      <div
        className="absolute inset-0 w-full h-[130%] -top-[10%]"
        style={{
          backgroundImage: `url(${CHILD_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${offset}px)`,
          willChange: "transform",
        }}
      />
      <div
        className="absolute inset-0"
        style={{ background: "rgba(31, 51, 41, 0.72)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p
            className="font-body italic tracking-[0.35em] text-xs uppercase mb-4"
            style={{ color: "#d4e8e0" }}
          >
            — Our Specialty —
          </p>
          <h2
            className="font-display font-bold text-white"
            style={{ fontSize: "clamp(2rem, 5.5vw, 3.5rem)" }}
            data-testid="child-section-title"
          >
            CHILD PSYCHOLOGY &amp; DEVELOPMENT
          </h2>
          <p
            className="font-body italic max-w-2xl mx-auto mt-6 text-base md:text-lg"
            style={{ color: "#e3efe9" }}
          >
            Dr. Fiza Hasny is deeply passionate about helping children thrive
            emotionally, socially, and cognitively — through play, patience, and
            evidence-based care.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -8 }}
              className="rounded-2xl p-7 md:p-8 transition-all duration-300"
              style={{
                background: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid rgba(255,255,255,0.22)",
                boxShadow: "0 14px 40px -18px rgba(0,0,0,0.4)",
              }}
              data-testid={`child-service-card-${i}`}
            >
              <div className="text-4xl mb-4">{s.icon}</div>
              <h3 className="font-display font-bold text-xl md:text-2xl mb-3 text-white">
                {s.title}
              </h3>
              <p
                className="font-body text-sm md:text-base leading-relaxed"
                style={{ color: "#e8f1ec" }}
              >
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
